import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Car, FileText, Download, Calendar, Phone } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DOC_LABELS = {
  carte_grise: 'Carte grise',
  attestation_assurance: 'Attestation d\'assurance',
  contrat_assurance: 'Contrat d\'assurance',
  controle_technique: 'Controle technique',
  facture: 'Facture',
  autre: 'Autre',
};

const VehicleCard = ({ vehicle, docs, onDownload }) => (
  <div className="card" style={{ padding: '20px', marginBottom: '16px' }} data-testid={`my-vehicle-${vehicle.id}`}>
    <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
      {vehicle.photo_url ? (
        <img src={`${API_URL}${vehicle.photo_url}`} alt={vehicle.brand} style={{ width: '120px', height: '80px', objectFit: 'cover', borderRadius: '8px' }} />
      ) : (
        <div style={{ width: '120px', height: '80px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#F4F5F7', borderRadius: '8px' }}>
          <Car size={36} color="#5E5F66" />
        </div>
      )}
      <div style={{ flex: 1, minWidth: '220px' }}>
        <h3 style={{ margin: 0, fontSize: '1.15rem' }}>{vehicle.brand} {vehicle.model}</h3>
        {vehicle.registration && <div style={{ fontSize: '0.95rem', fontWeight: 700, color: '#002FA7', letterSpacing: '0.6px', marginTop: '4px' }}>{vehicle.registration}</div>}
        <div style={{ fontSize: '0.82rem', color: '#5E5F66', marginTop: '6px' }}>
          {vehicle.vehicle_type === 'utilitaire' ? 'Utilitaire' : 'Tourisme'}
        </div>
        {(vehicle.insurance_end || vehicle.technical_control_date) && (
          <div style={{ display: 'flex', gap: '12px', marginTop: '10px', flexWrap: 'wrap' }}>
            {vehicle.insurance_end && (
              <div style={{ fontSize: '0.8rem', color: '#5E5F66', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Calendar size={14} /> Assurance jusqu'au <strong>{vehicle.insurance_end}</strong>
              </div>
            )}
            {vehicle.technical_control_date && (
              <div style={{ fontSize: '0.8rem', color: '#5E5F66', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Calendar size={14} /> CT <strong>{vehicle.technical_control_date}</strong>
              </div>
            )}
          </div>
        )}
        {vehicle.notes && <div style={{ fontSize: '0.82rem', color: '#5E5F66', marginTop: '8px', fontStyle: 'italic' }}>{vehicle.notes}</div>}
      </div>
    </div>

    <div style={{ marginTop: '20px', borderTop: '1px solid #E2E2E6', paddingTop: '14px' }}>
      <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#1E2030', marginBottom: '10px' }}>Documents ({docs.length})</div>
      {docs.length === 0 ? (
        <div style={{ fontSize: '0.85rem', color: '#5E5F66' }}>Aucun document disponible.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {docs.map(d => (
            <div key={d.id} data-testid={`my-vehicle-doc-${d.id}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', backgroundColor: '#F8F9FB', borderRadius: '8px', flexWrap: 'wrap', gap: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                <FileText size={18} color="#002FA7" />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: '0.88rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.title}</div>
                  <div style={{ fontSize: '0.72rem', color: '#5E5F66' }}>{DOC_LABELS[d.category] || d.category}</div>
                </div>
              </div>
              <button className="btn btn-secondary btn-sm" onClick={() => onDownload(vehicle.id, d)} data-testid={`download-doc-${d.id}`}><Download size={14} /> Telecharger</button>
            </div>
          ))}
        </div>
      )}
    </div>
  </div>
);

const MyVehiclePage = () => {
  const [vehicles, setVehicles] = useState([]);
  const [docsByVehicle, setDocsByVehicle] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/vehicles/my-vehicles`, { withCredentials: true });
        const list = res.data || [];
        setVehicles(list);
        // Fetch documents for each vehicle in parallel
        const docsResults = await Promise.all(list.map(v =>
          axios.get(`${API_URL}/api/vehicles/my-vehicles/${v.id}/documents`, { withCredentials: true }).then(r => [v.id, r.data]).catch(() => [v.id, []])
        ));
        setDocsByVehicle(Object.fromEntries(docsResults));
      } catch (err) {
        toast.error('Erreur lors du chargement de votre vehicule');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleDownload = async (vehicleId, doc) => {
    try {
      const res = await axios.get(`${API_URL}/api/vehicles/${vehicleId}/documents/download/${doc.id}`, { withCredentials: true, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.original_filename || doc.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (err) {
      toast.error('Erreur lors du telechargement');
    }
  };

  if (loading) return <div style={{ padding: '40px', textAlign: 'center', color: '#5E5F66' }}>Chargement...</div>;

  return (
    <div style={{ padding: '24px', maxWidth: '900px', margin: '0 auto' }} data-testid="my-vehicle-page">
      <h1 style={{ margin: '0 0 24px', fontSize: '1.8rem' }}>Mon vehicule</h1>
      {vehicles.length === 0 ? (
        <div className="card" style={{ padding: '40px', textAlign: 'center' }}>
          <Car size={56} color="#9E9EA7" style={{ marginBottom: '12px' }} />
          <h3 style={{ margin: '0 0 8px', color: '#5E5F66' }}>Aucun vehicule affecte</h3>
          <p style={{ margin: 0, fontSize: '0.9rem', color: '#9E9EA7' }}>
            Contactez votre responsable pour obtenir l'affectation d'un vehicule.
          </p>
        </div>
      ) : (
        vehicles.map(v => <VehicleCard key={v.id} vehicle={v} docs={docsByVehicle[v.id] || []} onDownload={handleDownload} />)
      )}
    </div>
  );
};

export default MyVehiclePage;
